import { IconButton, LuIcon } from "@overmap-ai/blocks"
import * as turf from "@turf/turf"
import type { Polygon } from "geojson"
import { memo, useCallback, useId, useMemo } from "react"
import type { MarkerDragEvent, MarkerEvent } from "react-map-gl/mapbox"

import { modelIconMapping } from "../../mappings/icons"
import { LINE_PAINT } from "../constants"
import { MapSlotLayerId } from "../enums"
import { Layer, Marker, Source } from "./memo"

interface PolygonEditorProps {
	polygon: Polygon
	onPolygonChange: (polygon: Polygon) => void
}

export const PolygonEditor = memo<PolygonEditorProps>((props) => {
	const { polygon, onPolygonChange } = props

	const id = useId()

	const centerPoint = useMemo(() => {
		return turf.centroid(turf.bboxPolygon(turf.bbox(polygon)))
	}, [polygon])

	const points = useMemo(() => {
		const points = turf.explode(polygon).features.map((feature) => feature.geometry)
		points.pop()
		return points
	}, [polygon])

	const midpointPoints = useMemo(() => {
		return points.map((point, pointIndex) => {
			const nextPoint = points[(pointIndex + 1) % points.length]
			return turf.midpoint(point, nextPoint).geometry
		})
	}, [points])

	const handleMove = useCallback(
		(e: MarkerDragEvent) => {
			const { lngLat } = e

			const from = centerPoint
			const to = turf.point(lngLat.toArray())

			const distance = turf.distance(from, to)
			const bearing = turf.bearing(from, to)

			onPolygonChange(turf.transformTranslate(polygon, distance, bearing))
		},
		[centerPoint, onPolygonChange, polygon],
	)

	const handlePointDrag = useCallback(
		(e: MarkerDragEvent, pointIndex: number) => {
			const { lngLat } = e

			const newPoint = turf.point(lngLat.toArray()).geometry

			const newPoints = points.map((point, index) => {
				return index === pointIndex ? newPoint : point
			})

			const newPolygon = turf.polygon([
				newPoints.map((point) => point.coordinates).concat([newPoints[0].coordinates]),
			]).geometry

			onPolygonChange(newPolygon)
		},
		[onPolygonChange, points],
	)

	const handleAddPoint = useCallback(
		(e: MarkerEvent<MouseEvent>, pointIndex: number) => {
			const { originalEvent } = e

			originalEvent.stopPropagation()

			const newPoints = [
				...points.slice(0, pointIndex + 1),
				midpointPoints[pointIndex],
				...points.slice(pointIndex + 1),
			]

			const newPolygon = turf.polygon([
				newPoints.map((point) => point.coordinates).concat([newPoints[0].coordinates]),
			]).geometry

			onPolygonChange(newPolygon)
		},
		[midpointPoints, onPolygonChange, points],
	)

	return (
		<>
			<Source id={`${id}-source`} type="geojson" data={polygon}>
				<Layer id={`${id}-line`} paint={LINE_PAINT} type="line" beforeId={MapSlotLayerId.DRAW} />
			</Source>

			<Marker
				anchor="center"
				longitude={centerPoint.geometry.coordinates[0]}
				latitude={centerPoint.geometry.coordinates[1]}
				draggable
				onDrag={handleMove}
			>
				<IconButton size="xs" radius="full" accentColor="info" className="flex!">
					<LuIcon icon={modelIconMapping.move} />
				</IconButton>
			</Marker>

			{midpointPoints.map((point, pointIndex) => (
				<Marker
					key={`midpoint-${pointIndex}`}
					anchor="center"
					longitude={point.coordinates[0]}
					latitude={point.coordinates[1]}
					onClick={(e) => {
						handleAddPoint(e, pointIndex)
					}}
				>
					<IconButton
						size="xs"
						radius="full"
						accentColor="info"
						className="flex! size-4! border border-white"
					>
						<LuIcon icon={modelIconMapping.add} />
					</IconButton>
				</Marker>
			))}

			{points.map((point, pointIndex) => (
				<Marker
					key={pointIndex}
					anchor="center"
					longitude={point.coordinates[0]}
					latitude={point.coordinates[1]}
					draggable
					onDrag={(e) => handlePointDrag(e, pointIndex)}
				>
					<IconButton
						size="xs"
						radius="full"
						accentColor="info"
						className="block! size-3! border border-white"
					/>
				</Marker>
			))}
		</>
	)
})
PolygonEditor.displayName = "PolygonEditor"
